import { atom, useAtom, useSetAtom } from 'jotai';
import {
  KnowledgeEventDefinitions,
  KnowledgeEvents,
} from 'lib-knowledge-remote-types/knowledgeEvents.types';
import { useCallback, useEffect } from 'react';

const DEBUG = false && process.env.NODE_ENV === 'development';

/**
 * This contains the registered event listeners. NOTE: at this stage there can
 * only be one listener registered for each event.
 */
const eventsAtom = atom<Partial<KnowledgeEvents>>({});

export function useRegisterEventsCallback(): (
  events: Partial<KnowledgeEvents>,
) => void {
  const setRegisteredEvents = useSetAtom(eventsAtom);

  return useCallback(
    (events: Partial<KnowledgeEvents>) => {
      setRegisteredEvents((registeredEvents) => ({
        ...registeredEvents,
        ...events,
      }));
    },
    [setRegisteredEvents],
  );
}

/**
 * Hook that allows event listeners to be registered
 *
 * @param events Mapping of keys to callback functions.
 */
export function useRegisterEvents(events: Partial<KnowledgeEvents>): void {
  // eslint-disable-next-line no-console
  if (DEBUG) console.log('useRegisterEvents', { events });

  const registerEventsCallback = useRegisterEventsCallback();

  useEffect(() => {
    registerEventsCallback(events);
  }, [registerEventsCallback, events]);
}

export type TriggerEvent = <A extends keyof KnowledgeEventDefinitions>(
  eventKey: A,
  payload: KnowledgeEventDefinitions[A],
) => void;

/**
 * You can trigger an events anywhere and if anyone has registered for this
 * event they will get triggered.
 *
 * @returns Function that allows you to trigger an event
 */
export function useTriggerEvent(): TriggerEvent {
  const [registeredEvents] = useAtom(eventsAtom);

  const triggerEvent = useCallback(
    <A extends keyof KnowledgeEventDefinitions>(
      eventKey: A,
      payload: KnowledgeEventDefinitions[A],
    ) => {
      // eslint-disable-next-line no-console
      DEBUG && console.log('Event: ', eventKey, payload);
      const listener = registeredEvents[eventKey];
      listener?.(payload);
    },
    [registeredEvents],
  );

  return triggerEvent;
}
